import React from "react";
import LegalDocument from "@/components/site/LegalDocument";

const content = String.raw`
These Terms of Service ("Terms") apply when you download, access or use the mobile application **Jantaquid** (the "App") and any related services (together, the "Service"), operated by **Kings Wake Digital Private Limited** ("Company", "we", "us", "our"), a company incorporated in India, with its website at https://kingswake.co.in.

These Terms constitute a legally binding agreement between you ("you", "user") and the Company.

---

## 1. Acceptance of Terms

By downloading, installing or using the App, you confirm that you have read, understood and agree to be bound by these Terms. If you do not agree, do not use the App and uninstall it.

We may update these Terms from time to time. Material changes will be notified within the App or on our website. Continued use of the App after changes take effect means you accept the revised Terms.

---

## 2. Eligibility

You represent that:

- You are at least 18 years of age. Under the Indian Contract Act, 1872, a contract with a minor is not enforceable. If you are under 18, you may use the App only under the supervision of a parent or legal guardian who agrees to these Terms on your behalf.
- Your use of the App does not violate any law or regulation applicable to you.
- You have the legal capacity to enter into these Terms.

---

## 3. Licence to Use

We grant you a limited, personal, non-exclusive, non-transferable, revocable licence to install and use the App on a device you own or control, for personal, non-commercial entertainment purposes only.

---

## 4. Privacy

Our Privacy Policy, available at https://kingswake.co.in/jantaquid/privacy-policy, forms part of these Terms. By using the App you agree to the collection and use of information as described in that policy.

---

## 5. Advertising

The App is free to use and is supported by advertising. Advertisements are served by third-party advertising networks and partners.

- Advertisements may be personalised based on your device's advertising identifier, subject to your device settings.
- We do not control the content of third-party advertisements and are not responsible for products, services or claims made in them.
- You may reset or limit your advertising identifier through your device settings.
- **Do not click on advertisements solely to obtain rewards.** Fraudulent or artificially generated ad activity is a breach of these Terms and of our advertising partners' policies, and may result in suspension of your account and forfeiture of any rewards.

---

## 6. In-Game Rewards and Linked Accounts

The App may allow you to link a rewards account operated by us and to earn in-game or promotional rewards through gameplay.

You acknowledge and agree that:

- Rewards have **no independent cash value**, are not a currency, and are not redeemable for money by the App itself.
- Rewards are promotional in nature and are granted at our sole discretion.
- We may change, suspend, reduce or discontinue rewards, reward rates, thresholds or eligibility at any time, without notice and without liability.
- Rewards are personal to you, non-transferable, and may not be sold, traded or assigned.
- Any rewards obtained through fraud, automation, modified or tampered software, multiple or fake accounts, emulators, or any exploitation of a bug or error may be **cancelled or reversed**, and your account may be suspended or terminated.
- You may link only one account, unless we expressly permit otherwise.
- Where rewards are delivered through a separate service or application, the terms of that service also apply.

Nothing in the App constitutes gambling, betting, or a game of chance. Rewards are earned through completion of gameplay tasks and are promotional only.

---

## 7. Prohibited Conduct

You agree not to:

- Reverse engineer, decompile, disassemble, modify, translate or create derivative works from the App or any part of it;
- Use cheats, bots, automation tools, modified clients, memory editors, emulators or any other software to gain an unfair advantage or to generate rewards;
- Circumvent, disable or interfere with security features of the App or any measure that limits use;
- Access the App through unofficial or tampered builds, or distribute such builds;
- Interfere with, disrupt or place an undue burden on our servers or networks;
- Impersonate any person or entity, or provide false information;
- Use the App for any unlawful purpose or in violation of any applicable Indian law;
- Sell, rent, sublicense or commercially exploit the App or access to it.

---

## 8. Intellectual Property

All content in the App — including code, artwork, graphics, level designs, audio, text, trademarks and logos ("Materials") — is owned by or licensed to the Company and is protected under the Copyright Act, 1957, the Trade Marks Act, 1999, and applicable international law.

You may not copy, reproduce, distribute, publicly display or create derivative works from the Materials except as expressly permitted by these Terms.

---

## 9. Third-Party App Stores

You may obtain the App through a third-party distribution platform such as the Google Play Store or Apple App Store (each an "App Store"). These Terms are between you and the Company, not with any App Store.

Your use of the App is also subject to the applicable App Store's terms. Where those terms conflict with these Terms, the more restrictive provision applies.

---

## 10. Third-Party Links and Services

The App may contain links to, or integrations with, third-party websites, advertisements or services. We do not control and are not responsible for their content, policies or practices. Review their terms and privacy policies before using them.

---

## 11. Availability, Updates and Termination

- We do not guarantee that the App will be available at all times or free from errors or interruptions.
- We may release updates, and you may be required to install an update to continue using the App.
- We may modify, suspend or discontinue the App, or any feature of it, at any time.
- We may suspend or terminate your access, without notice, if we reasonably believe you have breached these Terms.
- You may stop using the App at any time by uninstalling it.

---

## 12. Disclaimer of Warranties

To the maximum extent permitted by applicable law, the App and all Materials are provided **"as is"** and **"as available"**, without warranties of any kind, whether express or implied, including any implied warranty of merchantability, fitness for a particular purpose, or non-infringement.

We do not warrant that the App will be uninterrupted, secure, error-free, or that defects will be corrected. You use the App at your own risk.

Nothing in these Terms excludes or limits any rights you have under the Consumer Protection Act, 2019, which cannot be excluded by agreement.

---

## 13. Limitation of Liability

To the maximum extent permitted by applicable law:

- We will not be liable for any indirect, incidental, special, consequential or punitive damages, or for loss of profits, data, goodwill or rewards, arising from or relating to your use of the App.
- Our total aggregate liability to you for any claim arising out of or relating to these Terms or the App shall not exceed **INR 1,000** (one thousand Indian Rupees) or the amount you have paid to us in the twelve months preceding the claim, whichever is greater.

These limits do not apply to liability that cannot be excluded or limited under applicable Indian law.

---

## 14. Indemnity

You agree to indemnify and hold harmless the Company, its directors, employees and agents from any claim, demand, loss or expense (including reasonable legal fees) arising out of your breach of these Terms, your misuse of the App, or your violation of any law or third-party right.

---

## 15. Intellectual Property Complaints

If you believe that content in the App infringes your copyright or trademark, write to **Team@kingswake.co.in** with the subject line "IP Complaint", including:

- Your name, address and contact details;
- Identification of the right you claim is infringed;
- Identification of the material you claim is infringing and where it appears;
- A statement that you believe in good faith that the use is not authorised;
- A statement that the information you have provided is accurate and that you are the rights holder or authorised to act on their behalf;
- Your signature (physical or electronic).

---

## 16. Grievance Officer

In accordance with the Information Technology Act, 2000 and the Information Technology (Intermediary Guidelines and Digital Media Ethics Code) Rules, 2021, complaints regarding the App may be directed to:

**Grievance Officer**
Kings Wake Digital Private Limited
Email: **Team@kingswake.co.in**
Website: https://kingswake.co.in

We will acknowledge complaints within 24 hours and endeavour to resolve them within 15 days of receipt.

---

## 17. Governing Law and Dispute Resolution

These Terms are governed by and construed in accordance with the laws of India.

**Informal resolution first.** If you have a dispute with us, you agree to first contact us at Team@kingswake.co.in and attempt to resolve it informally. Most issues can be settled this way.

**Arbitration.** If a dispute is not resolved within thirty (30) days of written notice, it shall be referred to arbitration by a sole arbitrator under the Arbitration and Conciliation Act, 1996. The seat and venue of arbitration shall be **[CITY], India**, and the proceedings shall be conducted in English. The arbitrator's award shall be final and binding.

**Jurisdiction.** Subject to the above, the courts at **[CITY], India** shall have exclusive jurisdiction.

Nothing in this clause prevents you from approaching a consumer forum under the Consumer Protection Act, 2019.

---

## 18. General

- **Severability.** If any provision of these Terms is held invalid or unenforceable, the remaining provisions continue in full force.
- **No waiver.** Our failure to enforce any right or provision is not a waiver of that right or provision.
- **Assignment.** We may assign our rights and obligations under these Terms. You may not assign yours without our prior written consent.
- **Entire agreement.** These Terms, together with our Privacy Policy, constitute the entire agreement between you and us regarding the App.
- **No agency.** Nothing in these Terms creates a partnership, joint venture, employment or agency relationship between you and us.

---

## 19. Contact Us

**Kings Wake Digital Private Limited**
Email: **Team@kingswake.co.in**
Website: **https://kingswake.co.in**

---

*Placeholders to complete before publishing: registered office address, city for arbitration/jurisdiction, and CIN (company identification number).*
`;

export default function JantaquidTermsPage() {
  return <LegalDocument title="Jantaquid Terms & Conditions" description="Terms and conditions governing use of the Jantaquid mobile game from Kings Wake Digital Private Limited." content={content} alternate={{ href: "/jantaquid/privacy-policy", label: "Read the Privacy Policy" }} />;
}
